import React, { useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import MobileStepper from "@mui/material/MobileStepper";
import Button from "@mui/material/Button";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Tutorial Wrapper
import TutorialStep from "../TutorialStep";

// Images (Placeholders will be used until user uploads)
// We assume these paths based on user request
import scoutSearch from "assets/images/tutorial/platformscout-search.png";
import scoutAnalysis from "assets/images/tutorial/platformscout-analysis.png";
import scoutDiscovery from "assets/images/tutorial/platformscout-discovery.png";

function PlatformScoutIntroStep({ open, onComplete, onSkip }) {
    const [activeStep, setActiveStep] = useState(0);
    const maxSteps = 3;

    const steps = [
        {
            label: "Search & Scout",
            description: "Find any public profile across YouTube, Facebook, or Instagram. Just enter the username or URL to start scouting.",
            image: scoutSearch,
        },
        {
            label: "Deep Analysis",
            description: "Found a competitor? Click the Download icon on the search result card to instantly export their detailed profile for deep analysis.",
            image: scoutAnalysis,
        },
        {
            label: "Discover Trends",
            description: "Spot trending profiles and influencers in your niche to stay ahead of the competition.",
            image: scoutDiscovery,
        },
    ];

    const handleNext = () => {
        if (activeStep === maxSteps - 1) {
            onComplete(); // Finish on last step
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    return (
        <TutorialStep open={open} onClose={onSkip} maxWidth="lg">
            <MDBox p={2} display="flex" flexDirection="column" alignItems="center" textAlign="center">

                {/* Image Card with Header */}
                <MDBox
                    mb={2}
                    mt={-2}
                    p={0} // No padding on outer container to let header be flush
                    bgColor="grey-200"
                    borderRadius="xl"
                    display="flex"
                    flexDirection="column"
                    sx={{ width: "100%", height: "440px", overflow: "hidden" }} // Increased height for better fit
                >
                    {/* Header Bar */}
                    <MDBox
                        width="100%"
                        bgColor="info"
                        py={1.5}
                        textAlign="center"
                        style={{ borderRadius: "12px 12px 0 0" }} // Explicit top rounding
                    >
                        <MDTypography variant="subtitle1" color="white" fontWeight="medium" sx={{ lineHeight: 1, m: 0 }}>
                            How to use Platform Scout
                        </MDTypography>
                    </MDBox>

                    {/* Image Area */}
                    <MDBox
                        flexGrow={1}
                        width="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                    >
                        {/* Using text fallback if image fails to load during development */}
                        <MDBox
                            component="img"
                            src={steps[activeStep].image}
                            alt={steps[activeStep].label}
                            maxWidth="100%"
                            maxHeight="100%"
                            sx={{ objectFit: "contain" }}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                        />
                        <MDBox display="none">
                            <MDTypography variant="caption">Image: {steps[activeStep].label}</MDTypography>
                        </MDBox>
                    </MDBox>
                </MDBox>

                {/* content */}
                <MDTypography variant="h4" gutterBottom fontWeight="bold">
                    {steps[activeStep].label}
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ maxWidth: "80%", mb: 4 }}>
                    {steps[activeStep].description}
                </MDTypography>

                {/* Stepper Dots & Navigation */}
                <MDBox width="100%" display="flex" flexDirection="column" alignItems="center">
                    <MobileStepper
                        variant="dots"
                        steps={maxSteps}
                        position="static"
                        activeStep={activeStep}
                        sx={{
                            maxWidth: 400,
                            flexGrow: 1,
                            bgcolor: "grey.200",
                            borderRadius: "12px",
                            mb: 2,
                            p: 1,
                            justifyContent: "center"
                        }}
                        backButton={null}
                        nextButton={null}
                    />

                    <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%" mt={2}>
                        <MDButton
                            variant="text"
                            color="secondary"
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            startIcon={<KeyboardArrowLeft />}
                        >
                            Back
                        </MDButton>

                        <MDButton onClick={onComplete} variant="text" color="secondary" size="small">
                            Skip Tutorial
                        </MDButton>

                        <MDButton
                            variant="contained"
                            color="accent"
                            onClick={handleNext}
                            endIcon={activeStep === maxSteps - 1 ? undefined : <KeyboardArrowRight />}
                        >
                            {activeStep === maxSteps - 1 ? "Get Started" : "Next"}
                        </MDButton>
                    </MDBox>
                </MDBox>
            </MDBox>
        </TutorialStep>
    );
}

PlatformScoutIntroStep.propTypes = {
    open: PropTypes.bool.isRequired,
    onComplete: PropTypes.func.isRequired,
    onSkip: PropTypes.func.isRequired,
};

export default PlatformScoutIntroStep;
